import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Button, Text, Modal, Icon } from './index';
import { IconName } from './Icon/Icon.type';

export interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
}

interface FileExplorerProps {
  projectId: string;
  onFileSelect?: (file: FileNode) => void;
  selectedFile?: string;
  className?: string;
  refreshTrigger?: number;
  initialSelectedFile?: string | null;
  onInitialFileSelected?: () => void;
}

const FileExplorer: React.FC<FileExplorerProps> = ({
  projectId,
  onFileSelect,
  selectedFile,
  className = '',
  refreshTrigger = 0,
  initialSelectedFile,
  onInitialFileSelected
}) => {
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/dev-agent/projects/${projectId}/files`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const fileTree = response.data || [];
      setFiles(fileTree);

      // Start with all directories collapsed, but only if no initial file is being selected
      // (the initial file selection useEffect will expand the necessary directories)
      if (!initialSelectedFile) {
        setExpandedNodes(new Set());
      }
    } catch (err: any) {
      setError('Failed to load project files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadFiles();
    }
  }, [projectId, refreshTrigger]);

  // Helper function to normalize path separators (handle Windows backslash vs forward slash)
  const normalizePath = (path: string): string => {
    return path.replace(/\\/g, '/');
  };

  // Helper function to find a file node by path in the file tree
  const findFileByPath = (nodes: FileNode[], targetPath: string): FileNode | null => {
    const normalizedTarget = normalizePath(targetPath);
    
    for (const node of nodes) {
      const normalizedNodePath = normalizePath(node.path);
      
      if (normalizedNodePath === normalizedTarget) {
        return node;
      }
      if (node.type === 'directory' && node.children) {
        const found = findFileByPath(node.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to get all parent directory paths for a given file path
  const getParentPaths = (filePath: string): string[] => {
    // Normalize path first (handle both forward and back slashes)
    const normalizedPath = normalizePath(filePath);
    const parts = normalizedPath.split('/');
    const parentPaths: string[] = [];
    
    // Build up parent paths progressively
    for (let i = 1; i < parts.length; i++) {
      parentPaths.push(parts.slice(0, i).join('/'));
    }
    
    return parentPaths;
  };

  // Handle initial file selection when initialSelectedFile changes
  useEffect(() => {
    // Skip if no initial file, files not loaded, or no callback
    if (!initialSelectedFile || files.length === 0 || !onFileSelect) {
      return;
    }
    
    // Find the file node
    const fileNode = findFileByPath(files, initialSelectedFile);
    
    if (fileNode) {
      // Auto-expand all parent directories
      // Get normalized parent paths first
      const parentPaths = getParentPaths(fileNode.path);
      
      // Map to actual node paths in the tree (handles path separator differences)
      const actualParentPaths = parentPaths
        .map(p => {
          const parentNode = findFileByPath(files, p);
          return parentNode ? parentNode.path : null;
        })
        .filter((p): p is string => p !== null);
      
      // Add parent paths to existing expanded nodes (don't replace - keep other folders open)
      setExpandedNodes(prev => new Set([...prev, ...actualParentPaths]));
      
      // Select the file
      onFileSelect(fileNode);
      
      // Notify parent that initial file has been selected (so it can clear the state)
      // This allows the same file to be selected again from chat
      onInitialFileSelected?.();
    }
  }, [initialSelectedFile, files, onFileSelect, onInitialFileSelected]);

  const handleToggle = (path: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedNodes(newExpanded);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.post(`/projects/${projectId}/download`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project-${projectId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowDownloadConfirm(false);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  // TreeNode component
  const TreeNode: React.FC<{
    node: FileNode;
    level: number;
    onFileSelect?: (file: FileNode) => void;
    selectedFile?: string;
    onToggle: (path: string) => void;
    expandedNodes: Set<string>;
    isFirstDirectory?: boolean;
  }> = ({ node, level, onFileSelect, selectedFile, onToggle, expandedNodes, isFirstDirectory }) => {
    const isExpanded = expandedNodes.has(node.path);
    const isSelected = selectedFile === node.path;

    // Calculate padding based on level - consistent indentation per level
    // Base padding: 12px, then add 12px per level for consistent nesting
    const basePadding = 12; // px-bg-v2-lg
    const indentPerLevel = 12; // Consistent indent for each nesting level
    const leftPadding = basePadding + (level * indentPerLevel);
    
    // Use inline style for dynamic padding
    const paddingStyle = {
      paddingLeft: `${leftPadding}px`,
      paddingRight: '12px'
    };

    return (
      <div>
        <div
          className={`flex items-center gap-bg-v2-md py-bg-v2-sm rounded-bg-v2-sm hover:bg-bg-brand-primary-alt cursor-pointer transition-colors ${
            isSelected ? 'bg-bg-brand-primary-alt' : ''
          }`}
          style={paddingStyle}
          onClick={() => {
            if (node.type === 'directory') {
              onToggle(node.path);
            } else if (onFileSelect) {
              onFileSelect(node);
            }
          }}
        >
          {node.type === 'directory' && (
            <>
              <Icon
                name={IconName.ChevronRight}
                className={`w-bg-v2-xl h-bg-v2-xl shrink-0 text-fg-tertiary transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                inheritColor
              />
              <Icon
                name={isExpanded ? IconName.FolderOpen : IconName.FolderClosed}
                className="text-text-secondary w-bg-v2-xl h-bg-v2-xl shrink-0"
                inheritColor
              />
            </>
          )}
          {node.type === 'file' && (
            <>
              {/* Invisible chevron spacer to align with folders */}
              <div className="w-bg-v2-xl h-bg-v2-xl shrink-0" />
              <Icon
                name={IconName.File}
                className="text-text-secondary w-bg-v2-xl h-bg-v2-xl shrink-0"
                variant={isSelected ? 'selected' : undefined} inheritColor
              />
            </>
          )}
          <div className="flex-1 min-w-0" title={node.name}>
            {isSelected ? (
              <Text variant="text-sm" className="truncate" weight="semibold" color="secondary">
                {node.name}
              </Text>
            ) : (
              <Text variant="text-sm" className="truncate" color="secondary">
                {node.name}
              </Text>
            )}
          </div>
        </div>
        {node.type === 'directory' && isExpanded && node.children && (
          <div>
            {node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                level={level + 1}
                onFileSelect={onFileSelect}
                selectedFile={selectedFile}
                onToggle={onToggle}
                expandedNodes={expandedNodes}
                isFirstDirectory={false}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Main container styles matching Figma design
  // 
  // PROPERTIES NOT FOUND IN tailwind.config.js:
  // 1. width: 247px / minWidth: 247px - No exact match in spacing scale
  //    Closest: bg-v2-10xl (128px) or bg-v2-11xl (160px) - both too small
  //    Consider adding custom width token if this value is used elsewhere
  // 2. w-[15px] h-[15px] (line 255) - No exact match in spacing scale
  //    Closest: bg-v2-xl (16px) - slightly larger
  //    Consider adding custom size token or use w-4 h-4 (16px standard Tailwind)
  const containerStyle: React.CSSProperties = {
    width: '223px',
    minWidth: '247px',
    height: '100%',
  };

  if (error) {
    return (
      <div 
        className={`flex flex-col items-start gap-bg-v2-md self-stretch bg-bg-primary border-r border-border-secondary rounded-bl-bg-v2-xl rounded-tl-bg-v2-xl overflow-hidden h-full relative ${className}`}
        style={containerStyle}
      >
        <div className="px-bg-v2-lg pt-bg-v2-xl pb-bg-v2-md border-b border-border-secondary bg-bg-primary flex items-center justify-between w-full">
          <Text variant="text-xs" weight="semibold" color="secondary" className="tracking-wide">
            Codebase
          </Text>
        </div>
        <div className="flex items-center justify-center h-bg-v2-10xl bg-bg-primary">
          <div className="text-center">
            <Text variant="text-sm" color="error" className="mb-bg-v2-xs">Error loading files</Text>
            <Button
              variant="link"
              size="small"
              onClick={loadFiles}
              label="Try again"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className={`flex flex-col items-start gap-bg-v2-md self-stretch bg-bg-primary border-r border-border-secondary rounded-bl-bg-v2-xl rounded-tl-bg-v2-xl overflow-hidden h-full relative ${className}`}
        style={containerStyle}
      >
        <div className="flex items-center justify-between w-full px-bg-v2-xl pt-bg-v2-xl pb-bg-v2-xl border-b border-border-primary">
          <div className="flex items-center gap-bg-v2-xl">
            <Icon name={IconName.Layout} size="sm" variant="secondary" />
            <Text variant="text-sm" weight="semibold" color="primary">
              Codebase
            </Text>
          </div>
          <div className="flex items-center gap-bg-v2-md">
            <Button
              variant="ghost"
              size="very-small"
              onClick={() => setShowDownloadConfirm(true)}
              className="px-bg-v2-xs h-7 min-h-0 w-7"
              label={<Icon name={IconName.Download} className="w-bg-v2-xl h-bg-v2-xl text-fg-secondary" inheritColor />}
            />
            <Button
              variant="ghost"
              size="very-small"
              onClick={loadFiles}
              disabled={loading}
              className="px-bg-v2-xs h-7 min-h-0 w-7"
              label={<Icon name={IconName.Reload} className={`w-bg-v2-xl h-bg-v2-xl text-fg-secondary ${loading ? 'animate-spin' : ''}`} inheritColor />}
            />
          </div>
        </div>

        <div className="w-full bg-bg-primary overflow-y-auto flex-1 min-h-0 px-bg-v2-lg pb-bg-v2-xl modern-scrollbar">
          {loading ? (
            // Skeleton animation while loading
            <div className="py-bg-v2-xxs pb-bg-v2-3xl space-y-bg-v2-xs">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="flex items-center gap-bg-v2-md py-bg-v2-sm px-bg-v2-lg rounded-bg-v2-sm hover:bg-bg-primary-hover/30">
                  {/* Icon skeleton */}
                  <div className="w-bg-v2-xl h-bg-v2-xl skeleton-shimmer rounded shrink-0" />
                  {/* Text skeleton */}
                  <div 
                    className="h-4 skeleton-shimmer rounded"
                    style={{ width: `${Math.random() * 40 + 60}%` }}
                  />
                </div>
              ))}
            </div>
          ) : files.length === 0 ? (
            <div className="flex items-center justify-center h-bg-v2-10xl">
              <div className="text-center">
                <Icon name={IconName.FolderClosed} className="mx-auto mb-bg-v2-xs text-text-tertiary w-6 h-6" />
                <Text variant="text-sm" color="secondary">No files found</Text>
              </div>
            </div>
          ) : (
            <div className="py-bg-v2-xxs pb-bg-v2-3xl">
              {files.map((file) => (
                <TreeNode
                  key={file.path}
                  node={file}
                  level={0}
                  onFileSelect={onFileSelect}
                  selectedFile={selectedFile}
                  onToggle={handleToggle}
                  expandedNodes={expandedNodes}
                  isFirstDirectory={file.type === 'directory'}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showDownloadConfirm}
        onClose={() => setShowDownloadConfirm(false)}
        title="Download Project"
        size="sm"
        onSubmit={handleDownload}
        onCancel={() => setShowDownloadConfirm(false)}
        submitLabel={downloading ? 'Downloading...' : 'Download'}
        submitLoading={downloading}
        submitDisabled={downloading}
      >
        <div className="space-y-bg-v2-md">
          <Text color="secondary">
            Are you sure you want to download the project files as a ZIP?
          </Text>
          <div className="bg-bg-secondary-subtle p-bg-v2-sm rounded-bg-v2-md border border-border-secondary">
            <Text variant="text-sm" weight="medium" color="secondary">
              Check the README.txt file in the downloaded project for setup steps
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FileExplorer;